import { SelectInput } from './select.mjs';
import RemoteForm from '../controls/remoteFormPanel.mjs';
import {
	create,
	registerCustomElement,
	composedInputBuilder,
	createAddFirstAccountBtn,
	showNestedFieldsets,
	resolveAdminUrl,
} from '../utils.mjs';
import EVENTS from '../events.mjs';
import FormanConfig from '../formanConfig.mjs';
import { Picker } from '../controls/picker.mjs';
import { DEFAULT_THEME } from '../helpers/color.mjs';
import { I18n } from '../helpers/i18n.mjs';

const config = FormanConfig.config.fields.aiagent;

export class AiAgentInput extends SelectInput {
	constructor() {
		super();

		this.panel = null;
		this._firstCreationMode = false;
	}

	get allowApiCall() {
		return true;
	}

	get hasModeSwitch() {
		return false;
	}

	_shouldLoadOnBuild(options, value) {
		return typeof value !== 'undefined' && value !== '';
	}

	extendInstructions(instructions) {
		instructions.options = instructions.options || {};
		instructions.options.label = instructions.options.label || config.list.label;
		instructions.options.value = instructions.options.value || config.list.value;
		instructions.options.wrapper = instructions.options.wrapper || config.list.wrapper;
		instructions.options.placeholder = instructions.options.placeholder || I18n.l('aiAgent.choseAgent');

		const storeQuery = new URLSearchParams();

		if (!instructions.options.team) {
			instructions.options.team = this.getTeamId(instructions);
		}

		storeQuery.append('teamId', instructions.options.team);

		instructions.theme = instructions.theme || this.form.meta.module.package?.theme || DEFAULT_THEME;

		return instructions;
	}

	async _build(instructions, value, metadata) {
		const picker = create('imt-picker');

		picker.register(Picker.COMPONENTS.OPTION_RENDERER, (option, item) => {
			option.setAttribute('data-editable', item.editable);
		});

		// register events here, before picker RPC is called
		picker.addEventListener(EVENTS.PICKER.LOAD_DONE, (e) => {
			// TEMP: Don't load any nested instructions when there is a already a nested getAgent RPC
			const hasGetAgentRPC = instructions.options?.nested?.store;
			if (hasGetAgentRPC) {
				return;
			}

			for (const item of e.detail.store) {
				item.nested = [];

				if (item.scenarios) {
					item.nested.push({
						type: 'aiagenttools',
						agentId: item.id,
						name: `systemScenarios_${item.id}`,
						label: I18n.l('aiAgent.tools'),
						omit: true,
						listDescription: I18n.l('aiAgent.listDescription'),
						default:
							item.scenarios?.map((x) => {
								return { id: x.makeScenarioId };
							}) || [],
						options: {
							// TODO CDM-13800
							store: instructions.scenariolist?.options?.store,
						},
					});
				}

				if (item.mcpConfigs && item.mcpConfigs.length > 0) {
					item.nested.push({
						type: 'mcplist',
						name: `mcp_${item.id}`,
						label: I18n.l('aiAgent.mcp'),
						description: I18n.l('aiAgent.mcpDescription'),
						items: item.mcpConfigs,
						omit: true,
					});
				}

				if (item.contexts && item.contexts.length > 0) {
					item.nested.push({
						type: 'filelist',
						name: `contexts_${item.id}`,
						label: I18n.l('aiAgent.context'),
						description: I18n.l('aiAgent.contextDescription'),
						files: item.contexts.map((context) => ({
							name: context.metadata.documentName,
							type: context.metadata.type,
						})),
						omit: true,
					});
				}
			}
			this._firstCreationMode = !this.hasStoreItems;
			showNestedFieldsets(this, undefined, { restore: {} });
		});

		picker.addEventListener(EVENTS.PICKER.LOAD_ERROR, () => {
			this._firstCreationMode = false;
		});

		// Build the component

		metadata.build = Object.assign(metadata.build || {}, { picker });

		// Overwrite nested restore so that these are not restorable (which breaks the component)
		// It's enough to have all inputs with `omit: true` but some old blueprints might have been saved without it
		if (metadata.restore?.nested) {
			metadata.restore.nested = [];
		}

		await super._build(instructions, value, metadata);

		// Build composed input
		const settingsBtn = create(`a.ml-3.btn-link.dmo-text-primary ${I18n.l('aiAgent.configuration')}`);
		settingsBtn.prepend(create('i.far.fa-gear.mr-1'));
		settingsBtn.onclick = (e) => {
			const agentId = picker?.getOptionData()?.id;
			const url = agentId
				? `${this.getTeamId(instructions)}/agents/${agentId}`
				: `${this.getTeamId(instructions)}/agents`;

			window.open(resolveAdminUrl(url), '_blank');
		};
		this._label.append(settingsBtn);

		this.append(
			composedInputBuilder({
				picker,
				config,
				name: this.form.meta.module?.package?.name,
				theme: instructions.theme,
				buttons: [
					{
						label: I18n.l('aiAgent.createComposedBtn'),
						action: this._createHandler(instructions),
					},
				],
				readonly: this.readonly,
				image: instructions?.image,
			}),
		);

		this._firstCreationMode = !this.hasStoreItems;

		// Show connection creation btn if options list is empty
		if (this._firstCreationMode) {
			// Create button for account creation
			picker.before(
				createAddFirstAccountBtn(instructions.theme, I18n.l('aiAgent.createBtn'), this._createHandler(instructions)),
			);
		}
	}

	_createHandler(instructions) {
		return async (event) => {
			if (this.panel) return;

			this.disabled = true;

			this.panel = new RemoteForm(event.target);

			this.panel.addEventListener(EVENTS.FORM.SUBMITTED, async (event) => {
				this.disabled = false;

				const data = event.detail.data;
				const agent = {
					id: data.agentId,
					name: data.name,
				};

				// Need to reload full list after new agent is created so that tools are available
				await this.querySelector('imt-picker')?.reload();
				await this.addOption(agent);

				if (this._firstCreationMode) {
					this.querySelector('.create-connection')?.remove();
					this._firstCreationMode = false;
				}

				this.value = agent.id;
			});

			this.panel.addEventListener(EVENTS.PANEL.CLOSE, (event) => {
				this.disabled = false;
				this.panel = null;

				if (this._firstCreationMode) {
					const connectionCreationBtn = this.querySelector('.create-connection');

					if (connectionCreationBtn) {
						connectionCreationBtn.classList.remove('in-progress');
					}
				}
			});

			const instructionsQuery = new URLSearchParams();
			// const connectionId = this.form?.domains?.value('parameters')?.account;
			const teamId = this.getTeamId(instructions);
			const metadata = {};

			instructionsQuery.append('teamId', teamId);

			if (instructions.options?.team != null) {
				metadata.teamId = instructions.options.team;
			}

			await this.panel.build(
				{
					name: I18n.l('aiAgent.createAgentTitle'),
					src: `${config.instructions.create.path}?${instructionsQuery}`,
				},
				metadata,
			);

			this.panel.open();
		};
	}
}

registerCustomElement('imt-input-aiagent', AiAgentInput);
